import { getDateFormat, getTimeFormat } from '@griply/utils';
import { useSortedGoals } from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import { useCreateTag, useSortedTags } from 'features/tags';
import {
  useCreateTask,
  useDeleteTask,
  useHabits,
  useTaskById,
  useTaskTodayControls,
  useUpdateHabitScheduleEntry,
  useUpdateTask,
  useUpdateTaskRelationships,
} from 'features/tasks';
import { useUpdateHabitStatisticsTimeframe } from 'features/user';
import React, { useMemo } from 'react';
import { useOpenDeleteHabitDialog } from 'shared/contexts/delete-habit';
import { useOpenManualEntryLogging } from 'shared/contexts/habit-manual-entry-logging';
import { useOpenPremiumDialog } from 'shared/contexts/premium-dialog';
import { useCreateGoalsWithIdsMutation } from 'shared/hooks/queries/use-create-goals-with-ids-mutation';
import { useCreateTagsWithIdsMutation } from 'shared/hooks/queries/use-create-tags-with-ids-mutation';
import { useCreateTasksWithEntities } from 'shared/hooks/use-create-tasks-with-entities';
import { usePremiumFeatureAllowed } from 'shared/hooks/use-premium-feature-allowed';
import { useRelationships } from 'shared/hooks/use-relationships';
import { useUser } from 'shared/hooks/use-user';
import { NewGoalWithID } from 'shared/types/goal';
import { ID, IDPrefixes } from 'shared/types/id';
import { InsightsTimeframes } from 'shared/types/insights';
import { LimitKeys } from 'shared/types/limit-keys';
import { NewTagWithID } from 'shared/types/tag';
import { CreateTask, NewSubTask, NewTask } from 'shared/types/task';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';
import {
  MetaViewOptions,
  TaskCreate,
  TaskDetail,
} from 'shared/views/task-detail';

export type TaskDetailWrapperProps = {
  onClose: () => void;
  taskId?: ID;
  initialValues?: Partial<CreateTask>;
  dateAnchored?: boolean;
  openOnView?: MetaViewOptions;
};

export const TaskDetailWrapper: React.FC<TaskDetailWrapperProps> = ({
  onClose,
  taskId,
  initialValues,
  dateAnchored,
  openOnView,
}) => {
  const [newName, setNewName] = React.useState('');
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);
  const timeFormat = getTimeFormat(user);
  const dateFormat = getDateFormat(user);
  const timeframe =
    user?.settings?.habitStatisticsTimeframe ?? InsightsTimeframes.Week;

  const task = useTaskById(taskId);
  const habits = useHabits();
  const goals = useSortedGoals();
  const lifeAreas = useLocalizedLifeAreas();
  const tags = useSortedTags();
  const relationships = useRelationships();

  const updateHabitTimeframe = useUpdateHabitStatisticsTimeframe();

  const { submit: createTask } = useCreateTask();
  const bulkCreateTasks = useCreateTasksWithEntities();
  const { mutateAsync: createGoalsWithIds } = useCreateGoalsWithIdsMutation();
  const { mutateAsync: createTagsWithIds } = useCreateTagsWithIdsMutation();
  const { submit: updateTask } = useUpdateTask();
  const { submit: deleteTask } = useDeleteTask();
  const deleteHabit = useOpenDeleteHabitDialog();
  const openPremiumDialog = useOpenPremiumDialog();

  const activeGoals = useMemo(
    () =>
      goals.filter(
        ({ completedAt, archivedAt }) => !archivedAt && !completedAt,
      ),
    [goals],
  );
  const activeLifeAreas = useMemo(
    () => lifeAreas.filter(({ archivedAt }) => !archivedAt),
    [lifeAreas],
  );
  const activeHabits = useMemo(
    () =>
      habits.filter(
        ({ excludedFromLimits, deletedAt }) =>
          !excludedFromLimits && !deletedAt,
      ),
    [habits],
  );

  const { allowed: createGoalAllowed } = usePremiumFeatureAllowed(
    LimitKeys.CreateGoal,
    { totalUncompleted: activeGoals.length },
  );

  const { allowed: createHabitAllowed } = usePremiumFeatureAllowed(
    LimitKeys.CreateHabit,
    { total: activeHabits.length },
  );
  const { allowed: createHabitTargetAllowed } = usePremiumFeatureAllowed(
    LimitKeys.HabitScheduleTarget,
    {},
  );
  const { allowed: createHabitIconAllowed } = usePremiumFeatureAllowed(
    LimitKeys.HabitIcons,
    {},
  );
  const updateHabitEntry = useUpdateHabitScheduleEntry();
  const openManualEntryLogging = useOpenManualEntryLogging();
  const { onComplete } = useTaskTodayControls();

  const updateTagRelationships = useUpdateTaskRelationships(
    task?.id ?? '',
    IDPrefixes.Tag,
  );
  const { submit: createTag } = useCreateTag();

  const onCreateTask = async ({
    task: newTask,
    relationships,
    subTasks,
    goalsToCreate,
    tagsToCreate,
  }: {
    task: NewTask;
    relationships: Partial<Record<IDPrefixes, ID[] | null>>;
    subTasks: NewSubTask[];
    goalsToCreate: NewGoalWithID[];
    tagsToCreate: NewTagWithID[];
  }) => {
    const { tag } = relationships;
    // create goals from goalsToCreate
    const { goals: createdGoals, oldIdToNewIdMap: goalIdMap } =
      await createGoalsWithIds(goalsToCreate);
    // create tags from tagsToCreate
    const { oldIdToNewIdMap: tagIdMap } = await createTagsWithIds(tagsToCreate);

    // use the created goalId in the new task, if it was the linked to the new goal
    const _task = {
      ...newTask,
      goalId: goalIdMap[newTask.goalId!] ?? newTask.goalId,
    };

    const goal = [...goals, ...createdGoals].find(
      ({ id }) => id === _task.goalId,
    );
    // switch out the tag-ids from the tags that had to be created with the created tag-ids
    const tags = tag ? tag.map((id) => tagIdMap[id] ?? id) : tag;

    await createTask(_task, {
      goal,
      sectionId: initialValues?.sectionId ?? undefined,
      tags,
      subTasks,
    });

    onClose();
  };

  const onCreateMultipleTasks = async ({
    tasks,
    subTasks,
    goalsToCreate,
    tagsToCreate,
  }: {
    tasks: CreateTask[];
    subTasks: NewSubTask[];
    goalsToCreate: NewGoalWithID[];
    tagsToCreate: NewTagWithID[];
  }) => {
    await bulkCreateTasks({ tasks, subTasks, goalsToCreate, tagsToCreate });

    onClose();
  };

  const contextualCreationData = useMemo(
    () => ({
      goals: activeGoals,
      lifeAreas: activeLifeAreas,
      tags,
      relationships,

      timeframe,
      weekStartsOn,
      timeFormat,
      dateFormat,
      createHabitAllowed,
      createHabitTargetAllowed,
      createHabitIconAllowed,
      createGoalAllowed,

      openPremiumDialog,
      createTag,
      updateHabitTimeframe,
      updateHabitEntry,
      openManualEntryLogging,
    }),
    [
      activeGoals,
      activeLifeAreas,
      createGoalAllowed,
      createHabitAllowed,
      createHabitIconAllowed,
      createHabitTargetAllowed,
      createTag,
      dateFormat,
      openManualEntryLogging,
      openPremiumDialog,
      relationships,
      tags,
      timeFormat,
      timeframe,
      updateHabitEntry,
      updateHabitTimeframe,
      weekStartsOn,
    ],
  );

  return task ? (
    <TaskDetail
      onDelete={deleteTask}
      onDeleteHabit={deleteHabit}
      onUpdate={updateTask}
      onComplete={onComplete}
      onUpdateRelationships={updateTagRelationships}
      onClose={onClose}
      task={task}
      contextualCreationData={contextualCreationData}
      openOnView={openOnView}
    />
  ) : (
    <TaskCreate
      name={newName}
      updateName={setNewName}
      onCreate={onCreateTask}
      onCreateMultiple={onCreateMultipleTasks}
      onClose={onClose}
      contextualCreationData={contextualCreationData}
      initialValues={initialValues}
      dateAnchored={dateAnchored}
    />
  );
};
